/*
 * Settings > Import and export (draft 14): bring clients in from a Jobber or BookingKoala export (check first, then
 * import), and download every client or invoice as a spreadsheet.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, downloadFile } from "../../api/client";
import { useSession } from "../../app/session";
import { Choice } from "../../components/fields";
import { plural } from "../../components/format";
import { useToast } from "../../components/overlays";
import { Banner, Button, Card, StatusLabel } from "../../components/parts";
import { errText } from "../../components/work";

interface PlanRow {
  line: number;
  name: string | null;
  action: "add" | "update" | "skip";
  problems: string[];
}
interface Plan {
  rows: PlanRow[];
  add: number;
  update: number;
  skip: number;
}

const SOURCES = [
  { value: "jobber", label: "Jobber", hint: "Clients export" },
  { value: "bookingkoala", label: "BookingKoala", hint: "Customers export" },
  { value: "sheet", label: "My own sheet", hint: "Any CSV with a header row" },
] as const;

export function DataSettings() {
  const { can } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const [source, setSource] = useState<(typeof SOURCES)[number]["value"]>("jobber");
  const [csv, setCsv] = useState<string | null>(null);
  const [file, setFile] = useState("");
  const [plan, setPlan] = useState<Plan | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Plan | null>(null);

  const check = async (text: string) => {
    setBusy(true);
    try {
      setPlan(await api<Plan>("POST", "import/clients/check", { csv: text, source }));
    } catch (e) {
      toast(errText(e), { kind: "error" });
    } finally {
      setBusy(false);
    }
  };
  const run = async () => {
    if (!csv) return;
    setBusy(true);
    try {
      const r = await api<Plan>("POST", "import/clients", { csv, source });
      setDone(r);
      setPlan(null);
      setCsv(null);
      await qc.invalidateQueries({ queryKey: ["clients"] });
    } catch (e) {
      toast(errText(e), { kind: "error" });
    } finally {
      setBusy(false);
    }
  };
  const download = (what: "clients" | "invoices") => {
    const finish = toast(`Making the ${what} sheet`, { busy: true });
    void downloadFile(`export/${what}.csv`, `${what}.csv`)
      .then((ok) =>
        finish(ok ? `The ${what} sheet is downloaded` : "The preview has no server, so there is no file to download."),
      )
      .catch((e: unknown) => finish(errText(e), { kind: "error" }));
  };

  return (
    <>
      <Card title="Import clients">
        <p className="muted">
          Bring your clients in from Jobber or BookingKoala. Download their client export, then pick the file here. You see what
          will happen before anything is saved. Importing the same file again updates the clients, it never makes doubles.
        </p>
        {done ? (
          <Banner type="success">
            Done. {plural(done.add, "client")} added, {plural(done.update, "client")} updated, {plural(done.skip, "row")} skipped.
          </Banner>
        ) : null}
        <Choice label="Where the file is from" value={source} onChange={setSource} options={[...SOURCES]} />
        <label className="btn secondary">
          Pick a file
          <input
            type="file"
            accept=".csv,text/csv,.txt"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setFile(f.name);
              setDone(null);
              void f.text().then((text) => {
                setCsv(text);
                void check(text);
              });
            }}
          />
        </label>
        {file ? <p className="muted small">{file}</p> : null}
        {plan ? (
          <div className="stack">
            <p>
              <b>{plural(plan.add, "new client")}</b>, <b>{plural(plan.update, "client")}</b> updated,{" "}
              <b>{plural(plan.skip, "row")}</b> skipped.
            </p>
            {plan.rows.some((r) => r.problems.length) ? (
              <ul className="ag-list">
                {plan.rows
                  .filter((r) => r.problems.length)
                  .slice(0, 50)
                  .map((r) => (
                    <li key={r.line}>
                      <span>
                        Row {r.line}
                        {r.name ? `, ${r.name}` : ""}
                      </span>
                      <span className="muted small">{r.problems.join(". ")}</span>
                      <StatusLabel status={r.action === "skip" ? "critical" : "warning"}>
                        {r.action === "skip" ? "Skipped" : "Imported"}
                      </StatusLabel>
                    </li>
                  ))}
              </ul>
            ) : null}
            <div className="row">
              <Button loading={busy} disabled={plan.add + plan.update === 0} onClick={() => void run()}>
                Import {plural(plan.add + plan.update, "client")}
              </Button>
            </div>
          </div>
        ) : null}
      </Card>
      <Card title="Download a spreadsheet">
        <p className="muted">Every client or every invoice, to keep or to open in Excel or Google Sheets.</p>
        <div className="row">
          <Button type="secondary" icon="clients" onClick={() => download("clients")}>
            Clients
          </Button>
          {can("payment.record") ? (
            <Button type="secondary" icon="invoices" onClick={() => download("invoices")}>
              Invoices
            </Button>
          ) : null}
        </div>
      </Card>
    </>
  );
}
